<template>
  <div v-if="!loading">
    <b-container>
      <b-row
        align-content="center"
        align-h="center"
        :cols="hasStepGuide ? 6 : 3"
        class="mt-2 py-3"
      >
        <b-col
          cols="12"
          :md="hasStepGuide ? 11 : 7"
          :lg="hasStepGuide ? 9 : 5"
          class="text-center top-up-in-process"
        >
          <div class="header px-2 pb-1">
            <img
              alt="success"
              :src="require('@/assets/images/pages/payment/ic-payment-success-2.svg')"
            >
            <h3 class="my-1">
              {{ $t('addFundPage.creditCardPayment.textTopupSuccess') }}
            </h3>
            <p>{{ $t('addFundPage.creditCardPayment.subTitlePaymentSuccess') }}</p>
          </div>

          <div class="summary p-2 card">
            <h4
              id="title-summary-tazapay"
              data-testid="title-summary-tazapay"
              class="text-left mb-2"
            >
              {{ $t('addFundPage.creditCardPayment.textSummary') }}
            </h4>
            <div class="mb-1 d-flex flex-row flex-wrap justify-content-between">
              <div
                id="summary-tazapay-title-client"
                data-testid="summary-tazapay-title-client"
                class="font-14 text-placeholder"
              >
                {{ $t('addFundPage.creditCardPayment.textClient') }}
              </div>
              <strong
                id="summary-tazapay-current-client-name"
                data-testid="summary-tazapay-current-client-name"
              >
                {{ fullName }}
              </strong>
            </div>
            <div class="mb-1 d-flex flex-row justify-content-between">
              <div
                id="summary-tazapay-title-order-amount"
                data-testid="summary-tazapay-title-order-amount"
                class="font-14 text-placeholder"
              >
                {{ $t('payment.airWallex.labelOrderAmount') }}
              </div>
              <strong
                id="summary-tazapay-current-amount"
                data-testid="summary-tazapay-current-amount"
              >
                {{ currencyFormat(amount) }} USD
              </strong>
            </div>
            <div class="mb-1 d-flex flex-row justify-content-between">
              <div
                id="summary-tazapay-title-method"
                data-testid="summary-tazapay-title-method"
                class="font-14 text-placeholder"
              >
                {{ $t('addFundPage.creditCardPayment.textPaymentMethod') }}
              </div>
              <img
                id="summary-tazapay-current-method"
                data-testid="summary-tazapay-current-method"
                :src="tazapayLogo"
                height="16px"
                alt="tazapay"
              >
            </div>

            <div
              v-if="percentFee"
              class="d-flex flex-row justify-content-between mb-1"
            >
              <div
                id="summary-tazapay-title-service-charge"
                data-testid="summary-tazapay-title-service-charge"
                class="font-14 text-placeholder"
              >
                {{ $t('payment.balanceTransactionsTable.textServiceCharge') }}
              </div>
              <strong
                id="summary-tazapay-current-service-charge"
                data-testid="summary-tazapay-current-service-charge"
                class="text-primary"
              >{{ currencyFormat(serviceCharge) }} USD</strong>
            </div>

            <div
              v-if="methodFee"
              class="d-flex align-items-center justify-content-between mb-1"
            >
              <div
                id="summary-title-tazapay-fee"
                data-testid="summary-title-tazapay-fee"
                class="font-14 text-placeholder"
              >
                {{ $t('payment.paypal.textPaymentFee', { payment: 'Tazapay' }) }}
              </div>
              <div>
                <div
                  class="d-flex align-items-center"
                >
                  <strong
                    id="summary-current-tazapay-fee"
                    data-testid="summary-current-tazapay-fee"
                    class="text-primary"
                  >{{ currencyFormat(tazapayFee) }} USD</strong>
                </div>
              </div>
            </div>

            <div
              v-if="taxRate"
              class="d-flex flex-row justify-content-between mb-1"
            >
              <div
                id="summary-tazapay-title-tax-fee"
                data-testid="summary-tazapay-title-tax-fee"
                class="font-14 text-placeholder"
              >
                {{ $t('payment.balanceTransactionsTable.textTax') }}
              </div>
              <strong
                id="summary-tazapay-current-tax-fee"
                data-testid="summary-tazapay-current-tax-fee"
                class="text-primary"
              >{{ currencyFormat(tax) }} USD</strong>
            </div>

            <div
              v-if="tipAmount > 0"
              class="d-flex align-items-center justify-content-between"
            >
              <div
                id="summary-tazapay-title-tip-amount"
                data-testid="summary-tazapay-title-tip-amount"
                class="font-14 text-placeholder"
              >
                Tip
              </div>
              <strong
                id="summary-tazapay-current-tip-amount"
                data-testid="summary-tazapay-current-tip-amount"
                class="text-primary"
              >{{ currencyFormat(tipAmountTxt) }} USD</strong>
            </div>

            <hr class="hr-custom">

            <div class="d-flex align-items-center justify-content-between">
              <div
                id="summary-tazapay-title-total-amount"
                data-testid="summary-tazapay-title-total-amount"
                class="font-14 text-placeholder text-left"
              >
                {{ $t('addFundPage.creditCardPayment.textTotalAmount') }} = {{ $t('balance.textAmount') }}
                {{ percentFee ? ` + ${$t('payment.balanceTransactionsTable.textServiceCharge')}` : '' }}
                {{ taxRate ? ` + ${$t('payment.balanceTransactionsTable.textTax')}` : '' }}
                {{ methodFee ? ` + ${$t('payment.paypal.textPaymentFee', { payment: 'Tazapay' })}` : '' }}
                {{ tipAmount > 0 ? ' + Tip' : '' }}
              </div>
            </div>
            <h3
              id="summary-tazapay-current-total-amount"
              data-testid="summary-tazapay-current-total-amount"
              class="mt-2 text-left"
            >
              {{ currencyFormat(bankTransferAmount) }} USD
            </h3>
          </div>

          <div
            class="action mt-2 d-flex flex-column flex-sm-row justify-content-between align-items-center action-wrap"
          >
            <btn-loading
              v-ripple.400="'rgba(30, 30, 30, 0.15)'"
              id="btn-summary-tazapay-recharge"
              data-testid="btn-summary-tazapay-recharge"
              variant-convert="outline-dark"
              class="btn-sm-block btn-recharge"
              pill
              @click="$router.push('/payment/add-fund?tab=tazapay')"
            >
              {{ $t('payment.textRecharge') }}
            </btn-loading>

            <btn-loading
              id="btn-summary-tazapay-show-me-ad"
              data-testid="btn-summary-tazapay-show-me-ad"
              variant-convert="btn-submit"
              class="w-100 ml-2"
              pill
              @click="$router.history.push('/my-ads-account')"
            >
              {{ $t('addFundPage.creditCardPayment.textShowMeAdAccount') }}
            </btn-loading>
          </div>
          <p
            v-if="isOnlyEcomdyPlatform"
            class="text-center mt-1"
          >*{{ $t('addFundPage.creditCardPayment.textYouCanDownload') }}
            <span
              class="text-purple cursor-pointer underline"
              @click="$router.push('/payment?tab=payment_history')"
            >
              {{ $t('addFundPage.textHere') }}
            </span>
          </p>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>
<script>
import {
  BContainer, BRow, BCol,
} from 'bootstrap-vue'
import _get from 'lodash/get'
import { createNamespacedHelpers } from 'vuex'
import stepGuideMixin from '@/mixins/stepGuideMixin'
import numberFormatMixin from '@/mixins/numberFormatMixin'
import paymentMixin from '@/mixins/paymentMixin'
import gaTrackingMixin from '@/mixins/gaTrackingMixin'
import Ripple from 'vue-ripple-directive'
import { toastification } from '@core/mixins/toast'
import { PAYMENT_METHOD } from '@/constants'
import envMixin from '@/mixins/envMixin'
import BtnLoading from '@/layouts/components/BtnLoading.vue'
import authMixin from '@/mixins/authMixin'

const { mapGetters: mapGettersAuth } = createNamespacedHelpers('auth')
const { mapGetters } = createNamespacedHelpers('payment')

export default {
  components: {
    BtnLoading,
    BContainer,
    BRow,
    BCol,
  },

  directives: {
    Ripple,
  },

  mixins: [stepGuideMixin, numberFormatMixin, paymentMixin, toastification, envMixin, gaTrackingMixin, authMixin],

  data() {
    return {
      amount: this.$route.query.amount || '',
      bankTransferAmount: this.$route.query.bankTransferAmount || '',
      paymentMethod: this.$route.query.paymentMethod || '',
      historyTransaction: {},
    }
  },

  computed: {
    ...mapGettersAuth(['user', 'loading', 'generalConfigs']),
    ...mapGetters(['listBusinessAccountHistoryLimit']),

    fullName() {
      const fullName = _get(this.user, ['data', 'fullName'], '')
      const email = _get(this.user, ['data', 'email'], '')
      return fullName || email
    },

    tazapayLogo() {
      // eslint-disable-next-line global-require
      return require('@/assets/images/pages/payment/logo-tazapay.svg')
    },

    percentFee() {
      return this.user?.data?.plans?.transferFee?.tazapay || 0
    },

    methodFee() {
      return this.generalConfigs?.paymentGateFee?.tazapayGate || 0
    },

    serviceCharge() {
      return this.amountFormat(this.percentFee * this.amount)
        .toLocaleString('en-US')
    },

    tazapayFee() {
      return this.amountFormat(this.methodFee * this.amount)
        .toLocaleString('en-US') || 0
    },

    tax() {
      return this.amountFormat(this.taxRate * this.amount)
        .toLocaleString('en-US')
    },

    tipAmount() {
      return this.historyTransaction?.payload?.tipAMAmount || null
    },

    tipAmountTxt() {
      return this.amountFormat(this.tipAmount)
        .toLocaleString('en-US')
    },
  },

  watch: {
    listBusinessAccountHistoryLimit: {
      handler(transactions) {
        if (transactions.paging.total > 0) {
          this.historyTransaction = transactions.content.find(transaction => transaction.paymentMethod === PAYMENT_METHOD.TAZAPAY)
        }
      },
      deep: true,
      immediate: true,
    },
  },

  created() {
    const { fullPath } = this.$route
    const checkUrl = fullPath.toLowerCase()
      .includes('/payment/tazapay/success')

    if (this.isOnlyEcomdyPlatform) {
      this.gaTrackingUserAddFundSuccess(this.amount, this.paymentMethod) // GA Tracking
    }

    if (checkUrl && this.hasStepGuide) {
      if (this.isOnlyEcomdyPlatform) {
        this.gaTrackingUserStep(this.setupGuideStep) // GA Tracking
      }
      this.$router.push({ name: 'my-ads-account' })
      this.toastSuccess(this.$t('payment.toastTopUpSuccess'))
    }
  },
}
</script>

<style lang="scss" scoped>
.container-box {
  max-width: 340px;
  margin: 0 auto;
}

.summary {
  background-color: #fff;
  border-radius: var(--border-radius-base);
  padding: 24px 17px;
}

.btn-recharge {
  font-size: 14px;
  padding: 11px 20px;
  width: 150px;
}

.top-up-in-process {
  max-width: 410px;
  margin: 0;
  padding: 0;
}
</style>
